/**
 * Allys Basic Filters
 * 
 * Quick access filters displayed inline in the toolbar
 * Includes: Bedrooms, Bathrooms, and Price Range
 */

'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bed, Bathtub, CurrencyDollar, X } from '@phosphor-icons/react';

export interface BasicFilterValues {
  bedrooms?: string[];
  bathrooms?: string[];
  priceRange?: { min?: number; max?: number };
  foreignerEligible?: boolean;
}

export interface AllysBasicFiltersProps {
  /**
   * Current filter values
   */
  values: BasicFilterValues;
  /**
   * Callback when filters change
   */
  onChange: (values: BasicFilterValues) => void;
  /**
   * Labels for translations
   */
  labels?: {
    bedrooms?: string;
    bathrooms?: string;
    priceRange?: string;
    foreignerEligible?: string;
    anyBedrooms?: string;
    anyBathrooms?: string;
    min?: string;
    max?: string;
    apply?: string;
    clear?: string;
    selected?: string;
    bedroom?: string;
    bathroom?: string;
  };
  /**
   * Custom className
   */
  className?: string;
}

/**
 * AllysBasicFilters - Quick filter controls for toolbar
 */
export function AllysBasicFilters({
  values,
  onChange,
  labels = {
    bedrooms: 'Bedrooms',
    bathrooms: 'Bathrooms',
    priceRange: 'Price Range',
    foreignerEligible: 'Available to Foreigners',
    anyBedrooms: 'Any',
    anyBathrooms: 'Any',
    min: 'Min',
    max: 'Max',
    apply: 'Apply',
    clear: 'Clear',
    selected: 'Selected',
    bedroom: 'Bedroom',
    bathroom: 'Bathroom',
  },
  className = '',
}: AllysBasicFiltersProps) {
  const [showBedroomsDropdown, setShowBedroomsDropdown] = useState(false);
  const [showBathroomsDropdown, setShowBathroomsDropdown] = useState(false);
  const [showPriceDropdown, setShowPriceDropdown] = useState(false);
  
  // Temporary price values for the dropdown (not applied until user clicks Apply)
  const [tempPriceMin, setTempPriceMin] = useState<number | undefined>(values.priceRange?.min);
  const [tempPriceMax, setTempPriceMax] = useState<number | undefined>(values.priceRange?.max);

  const bedroomOptions = ['1', '2', '3', '4', '5+'];
  const bathroomOptions = ['1', '2', '3', '4+'];
  
  const handleBedroomToggle = (value: string) => {
    const current = values.bedrooms || [];
    const newBedrooms = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    
    const updatedValues: BasicFilterValues = {
      ...values,
    };
    
    if (newBedrooms.length > 0) {
      updatedValues.bedrooms = newBedrooms;
    } else {
      delete updatedValues.bedrooms;
    }
    
    onChange(updatedValues);
  };
  
  const handleBathroomToggle = (value: string) => {
    const current = values.bathrooms || [];
    const newBathrooms = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    
    const updatedValues: BasicFilterValues = {
      ...values,
    };
    
    if (newBathrooms.length > 0) {
      updatedValues.bathrooms = newBathrooms;
    } else {
      delete updatedValues.bathrooms;
    }
    
    onChange(updatedValues);
  };
  
  const handlePriceApply = () => {
    const updatedValues: BasicFilterValues = {
      ...values,
    };
    
    if (tempPriceMin !== undefined || tempPriceMax !== undefined) {
      const priceRange: { min?: number; max?: number } = {};
      if (tempPriceMin !== undefined) {
        priceRange.min = tempPriceMin;
      }
      if (tempPriceMax !== undefined) {
        priceRange.max = tempPriceMax;
      }
      updatedValues.priceRange = priceRange;
    } else {
      delete updatedValues.priceRange;
    }
    
    onChange(updatedValues);
    setShowPriceDropdown(false);
  };
  
  const getBedroomsLabel = () => {
    if (!values.bedrooms || values.bedrooms.length === 0) return labels.anyBedrooms;
    if (values.bedrooms.length === 1) return `${values.bedrooms[0]} ${labels.bedroom}`;
    return `${values.bedrooms.length} ${labels.selected}`;
  };
  
  const getBathroomsLabel = () => {
    if (!values.bathrooms || values.bathrooms.length === 0) return labels.anyBathrooms;
    if (values.bathrooms.length === 1) return `${values.bathrooms[0]} ${labels.bathroom}`;
    return `${values.bathrooms.length} ${labels.selected}`;
  };
  
  const getPriceLabel = () => {
    const { min, max } = values.priceRange || {};
    if (!min && !max) return labels.priceRange;
    if (min && max) return `$${(min/1000).toFixed(0)}k - $${(max/1000).toFixed(0)}k`;
    if (min) return `$${(min/1000).toFixed(0)}k+`;
    if (max) return `Up to $${(max/1000).toFixed(0)}k`;
    return labels.priceRange;
  };
  
  const hasActiveFilters = (values.bedrooms && values.bedrooms.length > 0) ||
                          (values.bathrooms && values.bathrooms.length > 0) ||
                          values.priceRange ||
                          values.foreignerEligible !== undefined;

  return (
    <div className={`flex items-center gap-3 flex-wrap ${className}`}>
      {/* Bedrooms Filter */}
      <div className="relative z-30">
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => {
            setShowBedroomsDropdown(!showBedroomsDropdown);
            setShowBathroomsDropdown(false);
            setShowPriceDropdown(false);
          }}
          className={`
            flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 border-2 font-inter
            ${(values.bedrooms && values.bedrooms.length > 0)
              ? 'bg-gradient-to-r from-[#243462] to-[#2a3d74] text-white border-[#243462] shadow-md'
              : 'bg-white text-gray-700 border-gray-200 hover:border-[#243462]/30 hover:shadow-md'
            }
          `}
        >
          <Bed size={16} weight="bold" />
          {getBedroomsLabel()}
          <motion.svg 
            className="w-4 h-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            strokeWidth={2.5}
            animate={{ rotate: showBedroomsDropdown ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </motion.svg>
        </motion.button>
        
        <AnimatePresence>
          {showBedroomsDropdown && (
            <>
              {/* Backdrop to close dropdown */}
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowBedroomsDropdown(false)}
              />
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full mt-2 left-0 z-50 bg-white rounded-xl shadow-2xl border-2 border-gray-200 p-4 min-w-[200px]"
              >
              <div className="space-y-2">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 font-inter">
                  {labels.bedrooms}
                </p>
                {bedroomOptions.map((option) => {
                  const isSelected = values.bedrooms?.includes(option);
                  return (
                    <button
                      key={option}
                      onClick={() => handleBedroomToggle(option)}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-all font-inter
                        ${isSelected
                          ? 'bg-gradient-to-r from-[#243462] to-[#2a3d74] text-white shadow-md'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }
                      `}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isSelected ? 'border-white bg-white' : 'border-gray-300'}`}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-[#243462]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      {option} {option === '1' ? labels.bedroom : labels.bedrooms}
                    </button>
                  );
                })}
              </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
      
      {/* Bathrooms Filter */}
      <div className="relative z-20">
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => {
            setShowBathroomsDropdown(!showBathroomsDropdown);
            setShowBedroomsDropdown(false);
            setShowPriceDropdown(false);
          }}
          className={`
            flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 border-2 font-inter
            ${(values.bathrooms && values.bathrooms.length > 0)
              ? 'bg-gradient-to-r from-[#243462] to-[#2a3d74] text-white border-[#243462] shadow-md'
              : 'bg-white text-gray-700 border-gray-200 hover:border-[#243462]/30 hover:shadow-md'
            }
          `}
        >
          <Bathtub size={16} weight="bold" />
          {getBathroomsLabel()}
          <motion.svg 
            className="w-4 h-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            strokeWidth={2.5}
            animate={{ rotate: showBathroomsDropdown ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </motion.svg>
        </motion.button>
        
        <AnimatePresence>
          {showBathroomsDropdown && (
            <>
              {/* Backdrop to close dropdown */}
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowBathroomsDropdown(false)}
              />
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full mt-2 left-0 z-50 bg-white rounded-xl shadow-2xl border-2 border-gray-200 p-4 min-w-[200px]"
              >
              <div className="space-y-2">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 font-inter">
                  {labels.bathrooms}
                </p>
                {bathroomOptions.map((option) => {
                  const isSelected = values.bathrooms?.includes(option);
                  return (
                    <button
                      key={option}
                      onClick={() => handleBathroomToggle(option)}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-all font-inter
                        ${isSelected
                          ? 'bg-gradient-to-r from-[#243462] to-[#2a3d74] text-white shadow-md'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }
                      `}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isSelected ? 'border-white bg-white' : 'border-gray-300'}`}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-[#243462]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      {option} {option === '1' ? labels.bathroom : labels.bathrooms}
                    </button>
                  );
                })}
              </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
      
      {/* Price Range Filter */}
      <div className="relative z-10">
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => {
            setShowPriceDropdown(!showPriceDropdown);
            setShowBedroomsDropdown(false);
            setShowBathroomsDropdown(false);
            // Reset temp values when opening
            if (!showPriceDropdown) {
              setTempPriceMin(values.priceRange?.min);
              setTempPriceMax(values.priceRange?.max);
            }
          }}
          className={`
            flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 border-2 font-inter
            ${values.priceRange
              ? 'bg-gradient-to-r from-[#243462] to-[#2a3d74] text-white border-[#243462] shadow-md'
              : 'bg-white text-gray-700 border-gray-200 hover:border-[#243462]/30 hover:shadow-md'
            }
          `}
        >
          <CurrencyDollar size={16} weight="bold" />
          {getPriceLabel()}
          <motion.svg 
            className="w-4 h-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            strokeWidth={2.5}
            animate={{ rotate: showPriceDropdown ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </motion.svg>
        </motion.button>
        
        <AnimatePresence>
          {showPriceDropdown && (
            <>
              {/* Backdrop to close dropdown */}
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowPriceDropdown(false)}
              />
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full mt-2 left-0 z-50 bg-white rounded-xl shadow-2xl border-2 border-gray-200 p-4 min-w-[280px]"
              >
              <div className="space-y-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide font-inter">
                  {labels.priceRange}
                </p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 font-inter">
                      {labels.min}
                    </label>
                    <input
                      type="number"
                      value={tempPriceMin || ''}
                      onChange={(e) => setTempPriceMin(e.target.value ? parseFloat(e.target.value) : undefined)}
                      placeholder="0"
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm font-semibold focus:border-[#243462] focus:ring-2 focus:ring-[#BBA339]/20 transition-all font-inter"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 font-inter">
                      {labels.max}
                    </label>
                    <input
                      type="number"
                      value={tempPriceMax || ''}
                      onChange={(e) => setTempPriceMax(e.target.value ? parseFloat(e.target.value) : undefined)}
                      placeholder="Any"
                      className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm font-semibold focus:border-[#243462] focus:ring-2 focus:ring-[#BBA339]/20 transition-all font-inter"
                    />
                  </div>
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handlePriceApply}
                  className="w-full px-4 py-2.5 bg-gradient-to-r from-[#243462] to-[#2a3d74] text-white font-bold rounded-lg transition-all shadow-md hover:shadow-lg font-inter"
                >
                  {labels.apply}
                </motion.button>
              </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
      
      {/* Foreigner Eligible Toggle */}
      <div className="flex items-center gap-3 px-4 py-2 bg-white border-2 border-gray-200 rounded-xl hover:border-[#243462]/30 transition-all">
        <label 
          htmlFor="foreigner-toggle" 
          className="flex items-center gap-3 cursor-pointer font-inter"
        >
          <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">
            {labels.foreignerEligible}
          </span>
          
          {/* Toggle Switch */}
          <button
            id="foreigner-toggle"
            type="button"
            role="switch"
            aria-checked={values.foreignerEligible || false}
            onClick={() => {
              const updatedValues: BasicFilterValues = {
                ...values,
              };
              
              if (values.foreignerEligible) {
                delete updatedValues.foreignerEligible;
              } else {
                updatedValues.foreignerEligible = true;
              }
              
              onChange(updatedValues);
            }}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#BBA339] focus:ring-offset-2
              ${values.foreignerEligible 
                ? 'bg-gradient-to-r from-[#BBA339] to-[#E4CE4A]' 
                : 'bg-gray-300'
              }
            `}
          >
            <motion.span
              layout
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform
                ${values.foreignerEligible ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        </label>
      </div>
      
      {/* Clear All Filters Button */}
      <AnimatePresence>
        {hasActiveFilters && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onChange({})}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all font-inter"
          >
            <X size={16} weight="bold" />
            {labels.clear}
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

